import { useState, useEffect } from "react";
import axios from "axios";

const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

interface Planimetry {
    planimetriaImageUrl: string | "";
    planimetriaDocumentUrl: string | "";
}

export interface PanoramicImage {
    id: number;
    urlBig: string;
    urlSmall: string;
    caption: string | null;
    position: number;
}

export interface SeasonalPrice {
    mese: string;
    anno: string;
    periodo_dal: string;
    periodo_al: string;
    tipo_periodo: 1 | 2 | 3 | 4; // 1: giornaliero, 2: settimanale, 3: quindicinale, 4: mensile
    prezzo: number;
    affittato: 0 | 1; // 0: non affittato, 1: affittato
}

export interface Video {
    id: number;
    videoUrl: string;
    virtualTour: boolean;
}

interface PropertyDetails {
    id: number;
    agencyId: number;
    seoString: string;
    statusId: number;
    category: number;
    exclusive?: boolean;
    verifiedProperty?: boolean;
    price: number;
    negotiable?: boolean;
    reservedNegotiation?: boolean;
    premises?: number;
    sqmt?: number;
    sqmtCommercial?: number;
    ref: string;
    referenceAgent?: {
        id: number;
        name: string;
        lastName: string;
    };
    siteClicks?: number;
    monthlyExpenses: string;
    descriptionDetails: {
        descriptionId: number;
        description: string;
        descriptionLang: string;
        descriptionTypeId: number;
        descriptionTypeName: string;
    }[];
    propertyStatusDetails: {
        propertyStatusId: number;
        propertyStatus: string;
        orderVisu: number;
    };
    contractDetails: {
        contractId: number;
        contractName: string;
    };
    auctionDetails: {
        auction: boolean;
        date: number;
        expired: boolean;
    };
    enerClassDetails?: {
        enerClassId: number;
        enerClassName: string;
        notes: string;
        orderVisu: number;
        afterOct2015: boolean;
    };
    performanceDetails: {
        summer: string;
        winter: string;
    };
    imagesDetails: {
        id: number;
        urlSmall: string | null;
        urlBig: string | null;
        urlLogo: string | null;
        main: boolean;
    }[];
    typologyDetails: {
        typologyId: number;
        it: string;
        en: string;
        de: string;
        fr: string;
        nl: string;
        es: string;
        ru: string;
        sq: string;
        zh: string;
        ja: string;
        seoString: string;
    };
    floors?: number;
    floorDetails?: {
        floorId: number;
        floor: string;
        labelApp: string;
        orderVisu: number;
    };
    elevator: boolean;
    apeDetails: {
        apeAfterOct2015: boolean;
        apeRin: string;
    };
    ipe?: number;
    address?: {
        id: number;
        propertyId: number;
        address: string;
        province: string;
        provinceAcronym: string;
        city: string;
        citySeoString: string;
        cityPostalCode: string;
        districtName: string;
        districtId: number;
        districtSeoString: string;
        fractionName: string;
        fractionId: number;
        fractionSeoString: string;
        latitude: number;
        longitude: number;
        googleAddress: string;
    };
    videos?: Video[];
    virtualTourData?: {
        virtualTourCasagest: string[];
        virtualTourExternal: string[];
    };
    planimetry?: Planimetry[];
    panoramicImages?: PanoramicImage[];
    //RESIDENZIALI
    residential?: {
        rooms: string;
        kingSizeBedRooms: number;
        doubleBedRooms: number;
        singleBedRooms: number;
        utilityRoom: boolean;
        utilityRoomSqmt: number;
        heatingDetails: {
            heatingId: number;
            heatingName: string | null;
            heatingFuelId: number;
            heatingFuelName: string | null;
            heatingTypeId: number;
            heatingTypeName: string | null;
        };
        kitchenDetails: {
            kitchenId: number;
            kitchenName: string | null;
        };
        carBoxDetails: {
            carBoxId: number | null;
            carBoxName: string | null;
        };
        carBoxSqmt: number | null;
        carPlaces: number | null;
        coveredCarPlaces: boolean;
        unclampedCarPlaces: boolean;
        garageDetails: {
            garage1: boolean;
            garage1Sqmt: number;
            garage2: boolean;
            garage2Sqmt: number;
        };
        bathrooms: number;
        bathroomCharacteristics: string | null;
        doubleBathrooms: number;
        gardenDetails: {
            garden: string | null;
            privateGarden: boolean;
            privateGardenSqmt: number;
            condominiumGarden: boolean;
            condominiumGardenSqmt: number;
            resede: boolean;
            resedeSqmt: number;
            otherLand: string | null;
            land: boolean;
            landSqmt: number;
        };
        conditionerDetails: {
            conditioner: boolean;
            otherConditioner: string | null;
        };
        furnitureDetails: {
            furnitureId: number;
            furnitureName: string | null;
            furnitureNotes: string | null;
        };
        terraceDetails: {
            terrace: boolean;
            terracesCharacteristics: string | null;
        };
        balcony: boolean;
        balconyCharacteristics: string | null;
        livingRoomDetails: {
            livingRoom: boolean;
            livingRoomSqmt: number;
        };
        diningRoomDetails: {
            diningRoom: boolean;
            diningRoomSqmt: number;
        };
        hallDetails: {
            hall: boolean;
            hallSqmt: number;
        };
        studioDetails: {
            studio: boolean;
            studioSqmt: number;
        };
        tavernDetails: {
            tavern: boolean;
            tavernSqmt: number;
        };
        mansardDetails: {
            mansard: boolean;
            mansardSqmt: number;
        };
        garretDetails: {
            garret: boolean;
            practicableGarret: boolean;
        };
        lastFloor: boolean;
        entryDetails: {
            indipendentEntry: boolean;
            traditionalEntry: boolean;
        };
        externalSpaces: string | null;
        areaDetails: {
            dayArea: string | null;
            nightArea: string | null;
        };
        bedPlacesDetails: {
            bedPlaces: number;
            bedPlacesDetails: string | null;
        };
        prestigeProperty: boolean;
        animalsAllowed: boolean;
        studentsAllowed: boolean;
        autoclave: boolean;
        alarm: boolean;
        waterSoftener: boolean;
        mosquitoNet: boolean;
        strongDoor: boolean;
        videophone: boolean;
        photovoltaicDetails: {
            photovoltaic: boolean;
            photovoltaicKw: number;
        };
        solarPanelsDetails: {
            solarPanels: boolean;
            solarPanelsKw: number;
        };
        miniWindTurbine: boolean;
        fireplace: boolean;
        swimmingPool: boolean;
        stonesDetails: {
            stones: boolean;
            stonesSqmt: number;
        };
    };
    //TERRENI
    serviceDetails?: {
        electricityAvailable?: number;
        gasAvailability?: number;
        roadAccess?: number;
        sewerSystem?: number;
        sidewalk?: number;
        telephoneLine?: number;
        waterAvailability?: number;
        publicLightning?: number;
    } | null;
    seasonalPrices?: SeasonalPrice[];
}

interface UseGetPropertyOptions {
    status?: number;
}

export const useGetProperty = (propertyId: number | null, options: UseGetPropertyOptions = {}) => {
    const [propertyDetails, setPropertyDetails] = useState<PropertyDetails | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {

        if (!propertyId) {
            setPropertyDetails(null);
            setLoading(false);
            return;
        }

        const fetchPropertyDetails = async () => {
            setLoading(true);
            setError(null);
            try {
                const requestBody = {
                    propertyId,
                    limit: 1,
                    offset: 0,
                    status: options.status
                };

                if (requestBody.status === undefined) {
                    delete (requestBody as Partial<typeof requestBody>).status;
                }

                const response = await axios.post(`${API_BASE_URL}/property`, requestBody);

                const property = response.data;
                if (!property || !property.propertyId) {
                    throw new Error("Dati della proprietà non validi.");
                }

                const formattedProperty: PropertyDetails = {
                    id: property.propertyId,
                    agencyId: property.agencyId,
                    seoString: property.seoString,
                    statusId: property.statusId,
                    category: property.category,
                    exclusive: property.esclusiva,
                    verifiedProperty: property.verifiedProperty,
                    price: property.price,
                    negotiable: property.trattabile,
                    reservedNegotiation: property.trattativa_riservata,
                    premises: property.premises,
                    sqmt: property.sqmt,
                    sqmtCommercial: property.sqmtCommercial,
                    ref: property.ref,
                    referenceAgent: property.referenceAgent ? {
                        id: property.referenceAgent.id,
                        name: property.referenceAgent.name,
                        lastName: property.referenceAgent.lastName
                    } : undefined,
                    siteClicks: property.clicksFromSite,
                    monthlyExpenses: property.monthlyExpenses || "",
                    descriptionDetails: property.descriptionDetails.map((desc: { descriptionId: any; description: any; descriptionLang: any; descriptionTypeId: any; descriptionTypeName: any; }) => ({
                        descriptionId: desc.descriptionId,
                        description: desc.description,
                        descriptionLang: desc.descriptionLang,
                        descriptionTypeId: desc.descriptionTypeId,
                        descriptionTypeName: desc.descriptionTypeName,
                    })),
                    propertyStatusDetails: {
                        propertyStatusId: property.propertyStatusDetails.propertyStatusId || 0,
                        propertyStatus: property.propertyStatusDetails.propertyStatus || "",
                        orderVisu: property.propertyStatusDetails.orderVisu || 0,
                    },
                    contractDetails: {
                        contractId: property.contractDetails.contractId || 0,
                        contractName: property.contractDetails.contractName || "",
                    },
                    auctionDetails: {
                        auction: property.auctionDetails.auction || false,
                        date: property.auctionDetails.date || 0,
                        expired: property.auctionDetails.expired || true,
                    },
                    enerClassDetails: {
                        enerClassId: property.enerClassDetails?.enerClassId || 0,
                        enerClassName: property.enerClassDetails?.enerClassName || "",
                        notes: property.enerClassDetails?.notes || "",
                        orderVisu: property.enerClassDetails?.orderVisu || 0,
                        afterOct2015: property.enerClassDetails?.afterOct2015 || false,
                    },
                    performanceDetails: {
                        summer: property.performanceDetails.summer || "",
                        winter: property.performanceDetails.winter || "",
                    },
                    imagesDetails: property.imagesDetails.map((img: any) => {
                        return {
                            ...img,
                        };
                    }),
                    typologyDetails: {
                        typologyId: property.typologyDetails.typologyId || 0,
                        it: property.typologyDetails.it || "",
                        en: property.typologyDetails.en || "",
                        de: property.typologyDetails.de || "",
                        fr: property.typologyDetails.fr || "",
                        nl: property.typologyDetails.nl || "",
                        es: property.typologyDetails.es || "",
                        ru: property.typologyDetails.ru || "",
                        sq: property.typologyDetails.sq || "",
                        zh: property.typologyDetails.zh || "",
                        ja: property.typologyDetails.ja || "",
                        seoString: property.typologyDetails.seoString || "",
                    },
                    floors: property.floors,
                    floorDetails: {
                        floorId: property.floorDetails?.floorId || 0,
                        floor: property.floorDetails?.floor || "",
                        labelApp: property.floorDetails?.labelApp || "",
                        orderVisu: property.floorDetails?.orderVisu || 0,
                    },
                    elevator: property.elevator,
                    apeDetails: {
                        apeAfterOct2015: property.apeDetails.apeAfterOct2015 || false,
                        apeRin: property.apeDetails.apeRin || "0.00",
                    },
                    ipe: property.ipe || "0.00",
                    address: {
                        address: property.indirizzo?.indirizzo || "",
                        districtName: property.indirizzo?.quartiereName || "",
                        districtId: property.indirizzo?.quartiereId || 0,
                        districtSeoString: property.indirizzo?.quartiereSeoString || "",
                        city: property.indirizzo?.comuneIt || "",
                        citySeoString: property.indirizzo?.comuneSeoString || "",
                        cityPostalCode: property.indirizzo?.cap || "",
                        fractionName: property.indirizzo?.frazioneName || "",
                        fractionId: property.indirizzo?.frazioneId || "",
                        fractionSeoString: property.indirizzo?.frazioneSeoString || "",
                        googleAddress: property.indirizzo?.indirizzo_google || "",
                        id: property.indirizzo?.id || 0,
                        latitude: property.indirizzo?.lat || null,
                        longitude: property.indirizzo?.lng || null,
                        propertyId: property.indirizzo?.property_id || null,
                        province: property.indirizzo?.provincia || "",
                        provinceAcronym: property.indirizzo?.provinciaSigla || "",
                    },
                    videos: property.videos?.map((video: any) => ({
                        id: video.id,
                        videoUrl: video.videoUrl,
                        virtualTour: video.virtualTour,
                    })) ?? [],
                    virtualTourData: property.virtualTourData ? {
                        virtualTourCasagest: property.virtualTourData.virtualTourCasagest || [],
                        virtualTourExternal: property.virtualTourData.virtualTourExternal || [],
                    } : undefined,
                    planimetry: property.planimetry,
                    panoramicImages: property.panoramicImages,
                    residential: property.residential ?? {
                        rooms: "",
                        doubleBedRooms: 0,
                        utilityRoom: 0,
                        utilityRoomSqmt: 0,
                        descriptionDetails: property.descriptionDetails.map((desc: { descriptionId: any; description: any; descriptionTypeId: any; descriptionTypeName: any; }) => ({
                            descriptionId: desc.descriptionId,
                            description: desc.description,
                            descriptionTypeId: desc.descriptionTypeId,
                            descriptionTypeName: desc.descriptionTypeName,
                        })),
                        heatingDetails: {
                            heatingId: 0,
                            heatingName: "",
                            heatingFuelId: 0,
                            heatingFuelName: "",
                            heatingTypeId: "",
                            heatingTypeName: "",
                        },
                        kitchenDetails: {
                            kitchenId: 0,
                            kitchenName: "",
                        },
                        carBoxDetails: {
                            carBoxId: property.carBox || 0,
                            carBoxName: property.carBoxDetails?.[0]?.carBoxName || null,
                        },
                        carPlaces: property.residential?.carPlaces || 0,
                        bathrooms: 0,
                        furnitureDetails: {
                            furnitureId: 0,
                            furnitureName: "",
                            furnitureNotes: null,
                        },
                        terraceDetails: {
                            terrace: 0,
                            terracesCharacteristics: null,
                        },
                        alarm: false,
                        mosquitoNet: false,
                        strongDoor: false,
                        videophone: false,
                        garden: null,
                        gardenDetails: {
                            garden: null,
                            privateGarden: false,
                            privateGardenSqmt: 0,
                            condominiumGarden: false,
                            condominiumGardenSqmt: 0,
                            resede: property.residential.resedeDetails?.resede,
                            resedeSqmt: property.residential.resedeDetails?.resedeSqmt,
                            otherLand: null,
                            land: false,
                            landSqmt: 0,
                        },
                        conditionerDetails: {
                            conditioner: false,
                            otherConditioner: null,
                        },
                        fireplace: false,
                        swimmingPool: false,
                        airConditioner: 0,
                        livingRoom: 0,
                        entryDetails: {
                            indipendentEntry: false,
                            traditionalEntry: false,
                        }
                    },
                    serviceDetails: property.serviceDetails ?? {
                        electricityAvailable: property.serviceDetails?.electricityAvailable || undefined,
                        gasAvailability: property.serviceDetails?.gasAvailability || undefined,
                        roadAccess: property.serviceDetails?.roadAccess || undefined,
                        sewerSystem: property.serviceDetails?.sewerSystem || undefined,
                        sidewalk: property.serviceDetails?.sidewalk || undefined,
                        telephoneLine: property.serviceDetails?.telephoneLine || undefined,
                        waterAvailability: property.serviceDetails?.waterAvailability || undefined,
                        publicLightning: property.serviceDetails?.publicLightning || undefined,
                    },
                    seasonalPrices: property.seasonalPrices,
                };
                setPropertyDetails(formattedProperty);

            } catch (err) {
                setError(err instanceof Error ? err.message : "Errore sconosciuto");
            } finally {
                setLoading(false);
            }
        };
        fetchPropertyDetails();
    }, [propertyId, options.status]);
    return { propertyDetails, loading, error };
};